import { useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { getModuleById } from "@/shared/constants/estateModules";
import { ELAYNE_TASK_IDS } from "@/shared/constants/constants";
import { AUTHORITY_TASK_IDS, type AuthorityTaskId } from "@/shared/constants/taskIds";
import { notifyTaskSubmitted } from "@/shared/utils/slackNotifications";
import { useNotificationFlags } from "@/shared/hooks/useNotificationFlags";
import { logActivity } from "@/shared/utils/activityLogger";
import type { FlowAnswers } from "@/shared/constants/flowTypes";
import type { TaskDescriptor } from "@/pages/postloss/modules/taskScreenRegistry";

interface EstateTaskRow {
  id: number;
  task_id: string;
  estate_id: number;
  status: string;
  answers: FlowAnswers;
  started_at: string | null;
  completed_at: string | null;
  last_active_at: string | null;
  updated_at: string;
}

interface UseEstateTasksOptions {
  moduleId: string;
  tasks: TaskDescriptor[];
  screenTaskId: Record<string, string>;
}

interface UseEstateTasksReturn {
  taskRows: EstateTaskRow[];
  isLoading: boolean;
  error: Error | null;
  initialAnswers: FlowAnswers;
  completedTaskIds: Set<string>;
  saveStep: (
    screenId: string,
    questionId: string | undefined,
    answer: string | string[] | undefined,
    allAnswers: FlowAnswers,
  ) => Promise<void>;
  completeTask: (taskId: string) => Promise<void>;
  completeModule: () => Promise<void>;
}

function buildQueryKey(estateId: number | undefined, moduleId: string) {
  return ["estateTasks", estateId, moduleId] as const;
}

export function useEstateTasks({
  moduleId,
  tasks,
  screenTaskId,
}: UseEstateTasksOptions): UseEstateTasksReturn {
  const { person, user } = useAppContext();
  const queryClient = useQueryClient();
  const { canSendNotification } = useNotificationFlags();
  const estateId = person?.estate_id;

  const module = getModuleById(moduleId);
  const moduleTaskIds = module?.tasks.map((t) => t.taskId) ?? [];
  const descriptorTaskIds = tasks.length > 0 ? [...new Set(tasks.map((t) => t.taskId))] : [];
  const taskIds = moduleTaskIds.length > 0 ? moduleTaskIds : descriptorTaskIds;

  const {
    data: taskRows = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: buildQueryKey(estateId, moduleId),
    queryFn: async () => {
      if (!estateId || taskIds.length === 0) return [];

      const { data, error: fetchError } = await supabase
        .from("estate_tasks")
        .select(
          "id, task_id, estate_id, status, answers, started_at, completed_at, last_active_at, updated_at",
        )
        .eq("estate_id", estateId)
        .in("task_id", taskIds);

      if (fetchError) throw fetchError;
      return (data ?? []) as EstateTaskRow[];
    },
    enabled: !!estateId && taskIds.length > 0,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const initialAnswers: FlowAnswers = useMemo(() => {
    const merged: FlowAnswers = {};
    const sorted = [...taskRows].sort((a, b) => {
      if (!a.last_active_at) return -1;
      if (!b.last_active_at) return 1;
      return a.last_active_at.localeCompare(b.last_active_at);
    });
    for (const row of sorted) {
      if (row.answers && typeof row.answers === "object") {
        Object.assign(merged, row.answers);
      }
    }
    delete merged.resumeScreen;
    return merged;
  }, [taskRows]);

  const completedTaskIds: Set<string> = useMemo(() => {
    const ids = new Set<string>();
    for (const row of taskRows) {
      if (row.status === "completed") {
        ids.add(row.task_id);
      }
    }
    return ids;
  }, [taskRows]);

  const saveMutation = useMutation({
    mutationFn: async ({
      taskId,
      answers,
      isLastScreen,
    }: {
      taskId: string;
      answers: FlowAnswers;
      isLastScreen: boolean;
    }) => {
      if (!estateId) return;

      const existingRow = taskRows.find((r) => r.task_id === taskId);
      const now = new Date().toISOString();

      const existingAnswers = (existingRow?.answers as Record<string, unknown>) ?? {};
      const mergedAnswers = { ...existingAnswers, ...answers };

      const isAlreadyCompleted = existingRow?.status === "completed";
      const isCompleting = (isLastScreen || isAlreadyCompleted) && !isAlreadyCompleted;
      const isElayneTask = (ELAYNE_TASK_IDS as readonly string[]).includes(taskId);

      const basePayload =
        isLastScreen || isAlreadyCompleted
          ? {
              answers: mergedAnswers,
              status: "completed",
              started_at: existingRow?.started_at ?? now,
              completed_at: existingRow?.completed_at ?? now,
              last_active_at: now,
            }
          : {
              answers: mergedAnswers,
              status: "in_progress",
              started_at: existingRow?.started_at ?? now,
              last_active_at: now,
            };

      const updatePayload =
        isCompleting && isElayneTask
          ? { ...basePayload, elayne_status: "pending" as const }
          : basePayload;

      const { error: upsertError } = await supabase
        .from("estate_tasks")
        .upsert(
          { estate_id: estateId, task_id: taskId, ...updatePayload },
          { onConflict: "estate_id,task_id" },
        );

      if (upsertError) throw upsertError;

      if (AUTHORITY_TASK_IDS.includes(taskId as AuthorityTaskId) && estateId && user?.user_id) {
        const allTaskAnswers: Record<string, string> = {};
        for (const row of taskRows) {
          const rowAnswers = row.answers as Record<string, unknown> | null;
          if (rowAnswers) {
            for (const [key, value] of Object.entries(rowAnswers)) {
              if (typeof value === "string") allTaskAnswers[key] = value;
            }
          }
        }
        for (const [key, value] of Object.entries(answers)) {
          if (typeof value === "string") allTaskAnswers[key] = value;
        }
      }

      if (isCompleting && isElayneTask && estateId) {
        const authEmail = (await supabase.auth.getUser()).data.user?.email;
        if (canSendNotification(authEmail)) {
          notifyTaskSubmitted({
            taskName: module?.title ?? taskId,
            taskId,
            estateId,
          }).catch((err) => console.warn("notifyTaskSubmitted failed:", err));
        }
      }

      if (isCompleting && estateId && user?.user_id) {
        logActivity({
          estate_id: estateId,
          action_type: "submitted_task",
          user_id: user.user_id,
          entity_type: "task",
          entity_id: taskId,
          metadata: {
            task_id: taskId,
            task_name: module?.title ?? taskId,
            module_id: moduleId,
          },
        }).catch((err) => console.warn("logActivity failed:", err));
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: buildQueryKey(estateId, moduleId) });
      if (variables.isLastScreen) {
        queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
      }
    },
  });

  const completeTaskMutation = useMutation({
    mutationFn: async (targetTaskId: string) => {
      if (!estateId) return;

      const row = taskRows.find((r) => r.task_id === targetTaskId);
      if (row?.status === "completed") return;

      const now = new Date().toISOString();
      const { error: upsertError } = await supabase.from("estate_tasks").upsert(
        {
          estate_id: estateId,
          task_id: targetTaskId,
          status: "completed",
          completed_at: now,
          last_active_at: now,
        },
        { onConflict: "estate_id,task_id" },
      );

      if (upsertError) throw upsertError;

      if (user?.user_id) {
        logActivity({
          estate_id: estateId,
          action_type: "completed_task",
          user_id: user.user_id,
          entity_type: "task",
          entity_id: targetTaskId,
          metadata: {
            task_id: targetTaskId,
            task_name: module?.title ?? targetTaskId,
            module_id: moduleId,
          },
        }).catch((err) => console.warn("logActivity failed:", err));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildQueryKey(estateId, moduleId) });
      queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
    },
  });

  const completeMutation = useMutation({
    mutationFn: async () => {
      if (!estateId) return;

      const now = new Date().toISOString();
      const pending = taskIds
        .filter((taskId) => {
          const row = taskRows.find((r) => r.task_id === taskId);
          return !row || row.status !== "completed";
        })
        .map((taskId) =>
          supabase.from("estate_tasks").upsert(
            {
              estate_id: estateId,
              task_id: taskId,
              status: "completed",
              completed_at: now,
              last_active_at: now,
            },
            { onConflict: "estate_id,task_id" },
          ),
        );

      await Promise.all(pending);

      if (user?.user_id) {
        logActivity({
          estate_id: estateId,
          action_type: "completed_module",
          user_id: user.user_id,
          metadata: {
            module_id: moduleId,
            module_name: module?.title ?? moduleId,
          },
        }).catch((err) => console.warn("logActivity failed:", err));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildQueryKey(estateId, moduleId) });
      queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
    },
  });

  const saveStep = useCallback(
    async (
      screenId: string,
      _questionId: string | undefined,
      _answer: string | string[] | undefined,
      allAnswers: FlowAnswers,
    ) => {
      const taskId = screenTaskId[screenId];
      if (!taskId) return;

      const taskAnswers: FlowAnswers = {};
      for (const [key, value] of Object.entries(allAnswers)) {
        const owningTask = screenTaskId[key];
        if (owningTask === taskId || !owningTask) {
          taskAnswers[key] = value;
        }
      }

      const existingRow = taskRows.find((r) => r.task_id === taskId);
      const isAlreadyCompleted = existingRow?.status === "completed";

      let isLastScreen = false;
      if (!isAlreadyCompleted) {
        const descriptor = tasks.find((t) => t.taskId === taskId);
        if (descriptor) {
          const visibleTaskScreens = descriptor.screens.filter((s) => {
            if (!s.shouldDisplay) return true;
            return s.shouldDisplay({ answers: allAnswers, currentStep: 0 });
          });
          const contentScreens = visibleTaskScreens.filter(
            (s) => !s.id.endsWith("_complete") && s.id !== "final_screen" && !s.skipOnBack,
          );
          const lastContentScreen =
            contentScreens.length > 0 ? contentScreens[contentScreens.length - 1] : null;
          isLastScreen = lastContentScreen?.id === screenId;
        }
      }

      await saveMutation.mutateAsync({ taskId, answers: taskAnswers, isLastScreen });
    },
    [screenTaskId, tasks, taskRows, saveMutation],
  );

  const completeTask = useCallback(
    async (targetTaskId: string) => {
      await completeTaskMutation.mutateAsync(targetTaskId);
    },
    [completeTaskMutation],
  );

  const completeModule = useCallback(async () => {
    completeMutation.mutate();
  }, [completeMutation]);

  return {
    taskRows,
    isLoading,
    error: error as Error | null,
    initialAnswers,
    completedTaskIds,
    saveStep,
    completeTask,
    completeModule,
  };
}
